import React from "react";
import { Badge } from "./Badge";
import { capitalize, classificationList } from "../../utils/helpers";
import { cn } from "../../utils/cn";

// The desires the classifier judges (#443) and the feed filters on, in the API's order — keep in
// step with `DESIRE_JUDGMENTS` there. The company toggles (`date`, `alone`) are judged too but not
// shipped, so they are not shown.
export const DESIRES = [
  ["dance", "Dance"],
  ["laugh", "Laugh"],
  ["think", "Think"],
  ["be_moved", "Be moved"],
  ["unwind", "Unwind"],
  ["get_energized", "Get energized"],
  ["something_dark", "Something dark"],
  ["meet_people", "Meet people"],
  ["hands_on", "Get hands-on"],
  ["play", "Play"],
];

const JUDGMENT_VALUES = ["yes", "partly", "no"];

// `yes` and `partly` both let an event through a desire filter; `yes` also ranks it higher.
const JUDGMENT_BADGE = { yes: "success", partly: "warning", no: "outline" };

function ClassificationField({ label, values }) {
  if (!values?.length) return null;
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {values.map((value) => (
          <Badge key={value} variant="outline" className="text-[10px] py-0">
            {value}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** Which prompt and model wrote a profile: what an admin needs to tell an old classification from a new one. */
function ProfileProvenance({ profile }) {
  const parts = [profile.prompt_version ? `Prompt v${profile.prompt_version}` : null, profile.classification_model];
  const text = parts.filter(Boolean).join(" · ");
  return text ? <p className="text-xs text-muted-foreground">{text}</p> : null;
}

function JudgmentsBlock({ judgments }) {
  if (!judgments) {
    return (
      <p className="text-xs text-muted-foreground">
        Not yet classified with judgments — it matches no desire filter until it is. Classify it again to get them.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
      {DESIRES.map(([id, label]) => (
        <div key={id} className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">{label}</span>
          {judgments[id] ? (
            <Badge variant={JUDGMENT_BADGE[judgments[id]] || "outline"} className="text-[10px] py-0">
              {judgments[id]}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A yes / partly / no choice per desire, for the artist editor. Native radios, so each desire is
 * one Tab stop and the arrow keys move within it — the behaviour a radio group announces.
 */
export function JudgmentPicker({ judgments, onChange }) {
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
      {DESIRES.map(([id, label]) => (
        <div key={id} className="flex items-center justify-between gap-2">
          <span className="text-sm">{label}</span>
          <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-input bg-background p-0.5">
            {JUDGMENT_VALUES.map((value) => (
              <label key={value} className="cursor-pointer">
                <input
                  type="radio"
                  name={`judgment-${id}`}
                  value={value}
                  checked={judgments[id] === value}
                  onChange={() => onChange({ ...judgments, [id]: value })}
                  className="peer sr-only"
                />
                <span
                  className={cn(
                    "block rounded px-2 py-0.5 text-xs capitalize transition-colors",
                    "peer-focus-visible:ring-2 peer-focus-visible:ring-ring",
                    judgments[id] === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  {value}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// The age range an event states (#754): "3–6", "18+", or "up to 10".
const ageRange = ({ age_min: min, age_max: max }) => {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `${min}+`;
  return max != null ? `up to ${max}` : null;
};

/** A read-only classification profile: what the feed filters and ranks on, and where it came from.
 * `curatedGenres` are an event's own (#754), stored beside its profile. */
export function ClassificationProfileDetail({ profile, defaultSource = "event", curatedGenres }) {
  if (!profile) return null;
  const age = ageRange(profile);
  return (
    <div>
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Classification</h4>
      <div className="space-y-3 rounded-md bg-muted/30 p-3">
        <div className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <p className="text-muted-foreground text-xs">Source</p>
            <p className="font-medium">{capitalize(profile.classification_source || defaultSource)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Primary type</p>
            <p className="font-medium">{capitalize(profile.primary_event_type || "other")}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Kids policy</p>
            <p className="font-medium">{capitalize(profile.kids_policy || "unknown")}</p>
          </div>
          {age && (
            <div>
              <p className="text-muted-foreground text-xs">Age</p>
              <p className="font-medium">{age}</p>
            </div>
          )}
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <p className="text-muted-foreground text-xs">Judgments</p>
            <ProfileProvenance profile={profile} />
          </div>
          <JudgmentsBlock judgments={profile.judgments} />
        </div>

        <div className="grid gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
          <ClassificationField label="Themes / subjects" values={classificationList(profile.themes_subjects)} />
          <ClassificationField label="Curated genres" values={classificationList(curatedGenres)} />
          <ClassificationField label="Modalities" values={classificationList(profile.experience_modalities)} />
          <ClassificationField label="Cultural identity" values={classificationList(profile.cultural_identity)} />
          <ClassificationField label="Target audience" values={classificationList(profile.target_audience)} />
          <ClassificationField label="Spoken language" values={classificationList(profile.spoken_language)} />
        </div>

        {profile.inferred_context && (
          <div>
            <p className="text-muted-foreground text-xs">Context</p>
            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{profile.inferred_context}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClassificationProfileDetail;
